import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PrivacySection from './components/PrivacySection';
import TermsSection from './components/TermsSection';
import SecuritySection from './components/SecuritySection';
import AboutSection from './components/AboutSection';
import CareersSection from './components/CareersSection';
import ContactSection from './components/ContactSection';

const EnterpriseFooterHub = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-foreground hover:text-primary transition-colors"
          >
            <Icon name="ArrowLeft" size={20} />
            <span className="font-semibold">Back to Home</span>
          </button>
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/login-authentication')}
            >
              Sign In
            </Button>
            <Button 
              variant="default" 
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => navigate('/login-authentication')}
            >
              Start Free Trial
            </Button>
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-96 h-96 bg-primary rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
            Enterprise Resources Hub
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12">
            Comprehensive legal documentation, security compliance, and company information for enterprise decision-makers
          </p>

          {/* Quick Navigation Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { 
                icon: 'Shield', 
                title: 'Privacy Policy', 
                description: 'GDPR-compliant data handling',
                href: '#privacy',
                color: 'text-success'
              },
              { 
                icon: 'FileText', 
                title: 'Terms of Service', 
                description: 'Clear legal framework',
                href: '#terms',
                color: 'text-primary'
              },
              { 
                icon: 'Lock', 
                title: 'Security & Compliance', 
                description: 'SOC2 Type II certified',
                href: '#security',
                color: 'text-warning'
              },
              { 
                icon: 'Users', 
                title: 'About Us', 
                description: 'Company mission & team',
                href: '#about',
                color: 'text-accent'
              },
              { 
                icon: 'Briefcase', 
                title: 'Careers', 
                description: 'Join our global team',
                href: '#careers',
                color: 'text-primary'
              },
              { 
                icon: 'MessageCircle', 
                title: 'Contact Support', 
                description: '24/7 enterprise assistance',
                href: '#contact',
                color: 'text-success'
              },
            ]?.map((item, index) => (
              <a
                key={index}
                href={item?.href}
                className="group bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6 hover:border-primary/50 hover:shadow-glow transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-full bg-${item?.color}/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <Icon name={item?.icon} size={24} className={item?.color} />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  {item?.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {item?.description}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>
      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-6 space-y-24 pb-20">
        <PrivacySection />
        <TermsSection />
        <SecuritySection />
        <AboutSection />
        <CareersSection />
        <ContactSection />
      </div>
      {/* Bottom CTA */}
      <section className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready to Transform Your Employee Experience?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join forward-thinking companies using First On Date
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="default" 
              size="lg"
              iconName="Rocket"
              iconPosition="right"
              onClick={() => navigate('/login-authentication')}
            >
              Start Free Trial
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => window.open('mailto:enterprise@firstondate.com', '_blank')}
            >
              Book Enterprise Demo
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnterpriseFooterHub;